import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Accent strip + icon tint. */
  tone?: "default" | "danger";
  size?: "sm" | "md" | "lg";
  /** Set false for destructive flows where a stray click shouldn't dismiss. */
  dismissOnBackdrop?: boolean;
};

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  tone = "default",
  size = "sm",
  dismissOnBackdrop = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape to dismiss, and Tab cycles within the panel rather than escaping to
  // the page behind it.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current = document.activeElement as HTMLElement | null;

    // bpmn-js binds keyboard shortcuts to `document`; capture phase keeps
    // typing in a dialog from also driving the diagram behind it.
    document.addEventListener("keydown", onKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the first field so a prompt dialog is immediately typeable.
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target =
        panel.querySelector<HTMLElement>("[data-autofocus]") ??
        panel.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [open, onKeyDown]);

  if (!open) return null;

  const labelId = title ? "schemunda-modal-title" : undefined;

  return createPortal(
    <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4">
      <div
        className="animate-fade-in absolute inset-0 bg-ink/35 backdrop-blur-[2px]"
        onClick={dismissOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        className={cn(
          "animate-modal-in relative w-full overflow-hidden",
          "bg-surface border-line rounded-xl border shadow-modal",
          SIZES[size],
        )}
      >
        {tone === "danger" && <div className="bg-danger h-1 w-full" />}

        {(title || description) && (
          <header className="px-5 pt-5 pb-1">
            {title && (
              <h2
                id={labelId}
                className="text-ink text-base leading-tight font-semibold"
              >
                {title}
              </h2>
            )}
            {description && (
              <p className="text-ink-muted mt-1.5 text-sm leading-relaxed">
                {description}
              </p>
            )}
          </header>
        )}

        {children && <div className="px-5 py-4">{children}</div>}

        {footer && (
          <footer className="bg-surface-sunken border-line flex items-center justify-end gap-2 border-t px-5 py-3.5">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
